import React from "react";
import { useRouterState } from "@tanstack/react-router";
import { isEqual, startCase } from "lodash";
import { ActiveSupplierTab } from "pages/app/SupplierSearchPage";

import { RouterTextLink } from "components/actions/RouterLink";
import { FrameworkCallOffTableCell } from "components/framework/FrameworkCallOffTableCell";
import { frameworkCallOffsUrl } from "components/framework/utils";
import { EMPTY_FILTERS } from "components/notices/utils";
import PaywallPopover from "components/paywall/PaywallPopover";
import { appRoutes, BuyerContext } from "lib/appRoutes";
import { useTableRowClickthrough } from "lib/core_components/commonTableItems";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { ColumnType, commonTableColumns } from "lib/core_components/Table/ColumnTypes";
import { IN_ROW_STAT_LINK, Table, tableRowSelection } from "lib/core_components/Table/Table";
import { useResetPageWhenPastEnd } from "lib/hooks/useURLState";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { EventData, EventNames, TrackingProvider, useTracking } from "lib/tracking";
import { EM_DASH } from "lib/utils";
import { toHumanRelativeDateFromNow } from "lib/utils/relative_time";
import { countryCodeToCountry } from "../../lib/data/optionItems";
import {
  OrganisationData,
  useOrganisations,
} from "../../lib/hooks/api/organisations/useOrganisations";
import { SupplierMentionType } from "../../lib/types/graphQLEnums";
import { SaveListDrawerProvider } from "../entities/save_components/lists/SaveListDrawerProvider";
import { SavedSuppliersEmpty } from "./SavedSuppliersEmpty";
import { SupplierNameCell } from "./SupplierNameCell";
import {
  AdditionalSupplierData,
  convertSupplierFiltersToOrganisationParams,
  getSupplierColumnCopy,
  isValidSortField,
  SortState,
  SupplierColumns,
  SupplierFilters,
} from "./utils";

import css from "./SupplierTable.module.scss";

function getSortOrder(key: string, sort?: SortState): "ascend" | "descend" | null {
  return sort?.field !== key ? null : sort.order === "ASC" ? "ascend" : "descend";
}

type SupplierTableData = OrganisationData & AdditionalSupplierData;

function getSupplierColumns({
  filters,
  authorised,
  onUnauthorisedClick,
  selectedColumns,
  additionalData,
  buyerContext,
  supplierTableTrackingData,
}: {
  filters: SupplierFilters;
  authorised: boolean;
  onUnauthorisedClick: () => void;
  selectedColumns: SupplierColumns[];
  additionalData: AdditionalSupplierData | undefined;
  /** Buyer-profile context. When set, the row, supplier name and award-count cells open the buyer↔supplier relationship subpage. Derived inside SupplierTable from the current URL. */
  buyerContext?: BuyerContext;
  supplierTableTrackingData?: EventData;
}) {
  const name: ColumnType<SupplierTableData> = {
    title: getSupplierColumnCopy("name"),
    dataIndex: "name",
    key: "name",
    sortDirections: ["ascend", "descend", "ascend"],
    sorter: false, // Will need to update backend to accept name sort if this is turned on
    sortOrder: getSortOrder("name", filters.sort),
    render: (_, s) => (
      <TrackingProvider data={{ "Supplier name": s.name }}>
        <SupplierNameCell supplierGuid={s.id} supplierName={s.name} buyerContext={buyerContext} />
      </TrackingProvider>
    ),
    sizeConfig: {
      small: 200,
      medium: 300,
      large: 400,
      xlarge: 500,
    },
  };

  const country: ColumnType<SupplierTableData> = {
    ...commonTableColumns.countryColumn,
    title: getSupplierColumnCopy("country"),
    dataIndex: "countryCode",
    key: "country",
    sortDirections: ["ascend", "descend", "ascend"],
    sorter: false,
    sortOrder: getSortOrder("country", filters.sort),
    render: (_, s) => (s.countryCode ? countryCodeToCountry(s?.countryCode) || EM_DASH : EM_DASH),
  };

  const sme: ColumnType<SupplierTableData> = {
    ...commonTableColumns.isSMEColumn,
    title: getSupplierColumnCopy("sme"),
    key: "sme",
    dataIndex: "isSme",
    sortDirections: ["ascend", "descend", "ascend"],
    sorter: false,
    sortOrder: getSortOrder("sme", filters.sort),
    render: (_, s) => {
      if (s.isSme) {
        return "Yes";
      } else if (s.isSme === false) {
        return "No";
      } else return EM_DASH;
    },
    sizeConfig: {
      small: 100,
      medium: 100,
      large: 100,
      xlarge: 100,
    },
  };

  const notices: ColumnType<SupplierTableData> = {
    key: "awards_count",
    dataIndex: "awardCount",
    title: getSupplierColumnCopy("awards_count"),
    sortDirections: ["descend", "ascend", "descend"],
    sorter: true,
    sortOrder: getSortOrder("awards_count", filters.sort),

    // Setting this to stop the default clickthrough event if user doesn't exactly click awards link
    onCell: (_rec) => ({
      onClick: (event) => event.stopPropagation(),
    }),

    render: (value, s) => (
      <PaywallPopover
        featureType="SUPPLIERS"
        showModalOnClick={false}
        contextSource="In-row"
        trackingData={supplierTableTrackingData}
      >
        {buyerContext ? (
          <div onClick={(e) => e.stopPropagation()}>
            <RouterTextLink
              {...appRoutes.buyerSupplier(buyerContext, s.id)}
              className={IN_ROW_STAT_LINK}
              eventName={EventNames.highlightClicked}
              eventAttributes={{
                "Highlight type": "Supplier awards",
              }}
            >
              {value}
            </RouterTextLink>
          </div>
        ) : (
          <RouterTextLink
            {...appRoutes.supplierAwards(s.id)}
            eventName={EventNames.highlightClicked}
            eventAttributes={{
              "Highlight type": "Supplier awards",
            }}
            className={IN_ROW_STAT_LINK}
            authorised={authorised}
            onUnauthorisedClick={onUnauthorisedClick}
          >
            {value}
          </RouterTextLink>
        )}
      </PaywallPopover>
    ),
    align: "left",
    sizeConfig: {
      small: 150,
      medium: 150,
      large: 175,
      xlarge: 175,
    },
  };

  const frameworkLots: ColumnType<SupplierTableData> = {
    key: "frameworkLots",
    title: getSupplierColumnCopy("frameworkLots"),
    render: (_, s) => {
      if (!additionalData?.frameworkData) {
        throw new Error(`"Lots" column requested but framework lot data wasn't provided.`);
      }

      const lots = additionalData.frameworkData.get(s.id)?.lots || [];
      return (
        <div className={css.lots}>
          {lots.map((lot) => (
            <EllipsisTooltipText
              key={lot.id}
              fullText={`${lot.id}: ${lot.title}`}
              containerClassname={css.lotName}
            />
          ))}
        </div>
      );
    },

    // TODO: Enable sorting for framework columns
    // sorter: true,
    // sortOrder: getSortOrder("frameworkLots", filters.sort),
    // sortDirections: ["descend", "ascend", "descend"],
    sizeConfig: {
      small: 240,
      medium: 240,
      large: 240,
      xlarge: 240,
    },
  };

  const frameworkLastActivity: ColumnType<SupplierTableData> = {
    key: "frameworkLastActivity",
    title: getSupplierColumnCopy("frameworkLastActivity"),
    render: (_, s) => {
      if (!additionalData?.frameworkData) {
        throw new Error(`"Lots" column requested but framework lot data wasn't provided.`);
      }
      const frameworkData = additionalData.frameworkData.get(s.id);
      const lastActivity = frameworkData?.callOffStats?.lastActivity;
      return lastActivity ? toHumanRelativeDateFromNow(lastActivity) : "--";
    },

    // TODO: Enable sorting for framework columns
    // sorter: true,
    // sortOrder: getSortOrder("frameworkLastActivity", filters.sort),
    // sortDirections: ["descend", "ascend", "descend"],
  };

  const frameworkCallOffs: ColumnType<SupplierTableData> = {
    key: "frameworkCallOffs",
    title: getSupplierColumnCopy("frameworkCallOffs"),
    render: (_, s) => {
      if (!additionalData?.frameworkData) {
        throw new Error(`"Lots" column requested but framework lot data wasn't provided.`);
      }
      const supplierFrameworkData = additionalData.frameworkData.get(s.id);
      const callOffStats = supplierFrameworkData?.callOffStats;

      if (!supplierFrameworkData) {
        return null;
      } else if (!callOffStats?.count) {
        return "--";
      }

      const { frameworkId } = supplierFrameworkData;

      return (
        <FrameworkCallOffTableCell
          averageAmount={callOffStats?.averageAmount.amount}
          callOffCount={callOffStats?.count}
          currency={callOffStats?.averageAmount.currency}
          linkTo={frameworkCallOffsUrl(frameworkId, {
            ...EMPTY_FILTERS,
            supplierGuids: [s.id],
            frameworkIds: [frameworkId],
            supplierMentionType: SupplierMentionType.DirectlyAwarded,
          })}
        />
      );
    },

    // TODO: Enable sorting for framework columns
    // sorter: true,
    // sortOrder: getSortOrder("frameworkCallOffs", filters.sort),
    // sortDirections: ["descend", "ascend", "descend"],
  };

  const columns = {
    sme,
    name,
    country,
    notices,
    frameworkLots,
    frameworkLastActivity,
    frameworkCallOffs,
  };

  return selectedColumns.map((c) => columns[c]).filter((col) => col !== undefined);
}

type Props = {
  filters: SupplierFilters;
  onSortChange?: (sort: SortState) => void;
  selectedColumns: SupplierColumns[];
  selectedRows: string[];
  onSelectedRowsChange?: (selectedIds: string[]) => void;
  additionalData?: AdditionalSupplierData;
  activeTab?: ActiveSupplierTab;
  setActiveTab?: (tab: ActiveSupplierTab) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
};

/**
 * Under `/buyers/$id/...` the row, supplier name and award-count cells open the buyer↔supplier
 * relationship subpage (e.g. `/buyers/123/supplier/abc`); `undefined` elsewhere, where they
 * link to the standalone supplier profile.
 */
function getBuyerContextFromPathname(pathname: string): BuyerContext | undefined {
  const match = pathname.match(/^\/buyers\/([^/]+)/);
  if (!match) return undefined;
  return { buyerId: match[1] };
}

export function SupplierTable({
  filters,
  onSortChange,
  selectedColumns,
  selectedRows,
  onSelectedRowsChange,
  additionalData,
  activeTab,
  setActiveTab,
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
}: Props) {
  const handleSelectionChange = (selectedKeys: string[]): void => {
    onSelectedRowsChange?.(selectedKeys);
  };
  const { isLoading, isError, data } = useOrganisations(
    convertSupplierFiltersToOrganisationParams(filters, pageSize, currentPage),
  );

  useResetPageWhenPastEnd(currentPage, pageSize, data?.searchOrganisations.total, setCurrentPage);

  const { authorised, checkSubscription: onUnauthorisedClick } = useCheckSubscription("SUPPLIERS", {
    "Context source": "In-row",
  });

  const { logEvent, trackingData } = useTracking();

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const buyerContext = React.useMemo(() => getBuyerContextFromPathname(pathname), [pathname]);

  const columns: ColumnType<SupplierTableData>[] = React.useMemo(() => {
    const supplierTableTrackingData = { ...trackingData, "CTA actioned": "Supplier" };

    return getSupplierColumns({
      filters,
      authorised,
      onUnauthorisedClick,
      selectedColumns,
      additionalData,
      buyerContext,
      supplierTableTrackingData,
    });
  }, [
    authorised,
    filters,
    additionalData,
    buyerContext,
    onUnauthorisedClick,
    selectedColumns,
    trackingData,
  ]);

  // Only on a buyer profile, matching the buyer table on a supplier profile.
  const onRow = useTableRowClickthrough<SupplierTableData>(
    buyerContext ? (s) => appRoutes.buyerSupplier(buyerContext, s.id) : undefined,
  );

  if (
    activeTab &&
    setActiveTab &&
    activeTab !== ActiveSupplierTab.ALL_SUPPLIERS &&
    !filters.guids?.length
  ) {
    return <SavedSuppliersEmpty activeTab={activeTab} setActiveTab={setActiveTab} />;
  }

  const orgs = data?.searchOrganisations.orgs ?? [];

  return (
    <SaveListDrawerProvider>
      <Table<SupplierTableData>
        ariaLabel="Supplier table"
        dataSource={orgs}
        columns={columns}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize,
          total: data?.searchOrganisations.total,
          onChange: (page, pageSize) => {
            setPageSize(pageSize);
            setCurrentPage(page);
            logEvent(EventNames.paginationSelected, { Value: page });
          },
          showSizeChanger: true,
        }}
        onChange={(_, __, sorter) => {
          if (Array.isArray(sorter)) {
            sorter = sorter[0];
          }
          // If the sort order is not defined, we don't want to trigger a sort without an order
          if (!filters.sort && !sorter.order) {
            return;
          }
          const sortField = isValidSortField(sorter.columnKey?.toString());
          if (sortField) {
            const newSortState: SortState = {
              order: sorter.order === "descend" ? "DESC" : "ASC",
              field: sortField,
            };
            if (!isEqual(filters.sort, newSortState)) {
              onSortChange?.(newSortState);
              // TODO: maybe move all tracking?
              logEvent(EventNames.tableSorted, {
                Context: "Column action",
                "Sorted by": startCase(newSortState.field),
                "Sort order": newSortState.order,
              });
            }
          }
        }}
        rowSelection={tableRowSelection(selectedRows, handleSelectionChange, orgs, "id")}
        rowKey="id"
        onRow={onRow}
        isError={isError}
      />
    </SaveListDrawerProvider>
  );
}
